// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 207e54e9-7c77-4957-aba2-542b2a4cdee7

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Stack, TextField, Button, IconButton, Chip, Tooltip,
  Table, TableHead, TableRow, TableCell, TableBody, Alert, CircularProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import HelpBubble from './HelpBubble';

// Releases of one product, with the vendor dates that drive risk.
//
// This is the VENDOR's answer to "what versions exist". A client's own
// application record has a version field, and that is a different fact: what
// that one client happens to run. Keeping them apart is non-negotiable 8 in
// practice, and it matters here because a client sitting on a 2019 release
// does not make 2019 the current one.
//
// An end-of-support date is the single most consequential thing in this table:
// it drives wave order and the migration business case. So the row carries its
// citation -- URL, verbatim quote, retrieval date -- and the form asks for them
// beside the date rather than in a separate screen nobody opens. A date with no
// source is somebody's memory of a date, and it will be quoted at a client.

const BLANK = {
  id: null, version: '', releaseDate: '', eolDate: '', eosDate: '',
  notes: '', sourceUrl: '', sourceQuote: '', sourceRetrieved: '',
};

// Rendered as a chip so the state is readable at a glance across a list of
// releases. Compared against today rather than stored, because "supported" is
// a fact about the date and the calendar, not a field that can go stale.
function supportState(v) {
  const today = new Date().toISOString().slice(0, 10);
  if (v.eosDate && v.eosDate <= today) return { label: 'Support ended', color: 'error' };
  if (v.eolDate && v.eolDate <= today) return { label: 'End of life', color: 'warning' };
  if (v.eosDate) return { label: `Supported to ${v.eosDate}`, color: 'success' };
  if (v.eolDate) return { label: `EOL ${v.eolDate}`, color: 'default' };
  return { label: 'No dates recorded', color: 'default' };
}

// pendingRef is how the ENTRY dialog's Save reaches a half-filled version.
//
// Frank, 2026-08-20: "right now I add info in version, hit save, no errors but
// it shows nothing I added." The cause was mine: this panel sits inside the
// catalogue entry dialog, which has its own Save in its DialogActions, so there
// were two Save buttons on one screen. Filling the version form and clicking
// the obvious one at the bottom saved the ENTRY and dropped the version draft,
// silently, because nothing had gone wrong -- a different form had been
// submitted than the one being filled in.
//
// Rather than teach people which button is which, the outer Save now flushes
// whatever is in here first. The panel writes its draft into pendingRef and
// exposes a flush; saveEntry calls it before it closes. Both buttons then do
// what somebody filling in a form expects, which is the only acceptable
// outcome for two buttons that both say Save.
export default function CatalogVersions({ api, catalogAppId, appName, pendingRef }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!catalogAppId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    try {
      setRows(await api(`/catalog/versions?catalogAppId=${encodeURIComponent(catalogAppId)}`));
      setErr('');
    } catch (e) {
      setErr(e.message || 'could not load versions');
    } finally { setLoading(false); }
  }, [api, catalogAppId]);

  useEffect(() => { load(); }, [load]);

  // Published on every render so the parent never holds a stale closure over an
  // older draft. flush() is a no-op when there is nothing half-filled, so the
  // outer Save can call it unconditionally.
  useEffect(() => {
    if (!pendingRef) return undefined;
    pendingRef.current = {
      hasDraft: !!(draft && draft.version && draft.version.trim()),
      flush: async () => {
        if (!draft || !draft.version || !draft.version.trim()) return false;
        await api('/catalog/versions', {
          method: 'PUT', body: JSON.stringify({ ...draft, catalogAppId }),
        });
        setDraft(null);
        await load();
        return true;
      },
    };
    return () => { if (pendingRef) pendingRef.current = null; };
  }, [draft, api, catalogAppId, load, pendingRef]);

  async function save() {
    setBusy(true);
    try {
      await api('/catalog/versions', {
        method: 'PUT', body: JSON.stringify({ ...draft, catalogAppId }),
      });
      setDraft(null);
      await load();
    } catch (e) { setErr(e.message || 'could not save'); } finally { setBusy(false); }
  }

  async function remove(id) {
    try {
      await api(`/catalog/versions?id=${encodeURIComponent(id)}`, { method: 'DELETE' });
      await load();
    } catch (e) { setErr(e.message || 'could not delete'); }
  }

  // A catalogue entry that has not been saved yet has no id to hang versions
  // off. Saying so beats rendering an Add button that would fail.
  if (!catalogAppId) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        Save this entry first, then its releases can be recorded here.
      </Typography>
    );
  }
  if (loading) return <Box sx={{ py: 2 }}><CircularProgress size={22} /></Box>;

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Versions <HelpBubble topic="catalogVersions" />
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button size="small" startIcon={<AddIcon />} onClick={() => setDraft({ ...BLANK })}>
          Add version
        </Button>
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setErr('')}>{err}</Alert>}

      <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
        Which releases of {appName || 'this product'} exist, and when the vendor stops supporting
        them. This is the vendor&apos;s answer, not what any one client is running.
      </Typography>

      {!rows.length && !draft && (
        <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic', mb: 1 }}>
          No releases recorded yet.
        </Typography>
      )}

      {!!rows.length && (
        <Table size="small" sx={{ mb: 2 }}>
          <TableHead>
            <TableRow sx={{ '& th': { fontWeight: 700 } }}>
              <TableCell>Version</TableCell>
              <TableCell>Released</TableCell>
              <TableCell>EOL</TableCell>
              <TableCell>End of support</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Source</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((v) => {
              const st = supportState(v);
              return (
                <TableRow key={v.id} hover>
                  <TableCell sx={{ fontWeight: 600 }}>{v.version}</TableCell>
                  <TableCell>{v.releaseDate || '—'}</TableCell>
                  <TableCell>{v.eolDate || '—'}</TableCell>
                  <TableCell>{v.eosDate || '—'}</TableCell>
                  <TableCell><Chip size="small" color={st.color} label={st.label} /></TableCell>
                  <TableCell>
                    {/* An uncited date is shown AS uncited rather than looking
                        the same as one somebody verified. */}
                    {v.sourceUrl
                      ? <Tooltip title={v.sourceQuote || 'no quote recorded'}>
                          <a href={v.sourceUrl} target="_blank" rel="noreferrer">vendor</a>
                        </Tooltip>
                      : <Typography variant="caption" sx={{ color: 'warning.main' }}>uncited</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => setDraft({
                        id: v.id, version: v.version,
                        releaseDate: v.releaseDate || '', eolDate: v.eolDate || '',
                        eosDate: v.eosDate || '', notes: v.notes || '',
                        sourceUrl: v.sourceUrl || '', sourceQuote: v.sourceQuote || '',
                        sourceRetrieved: v.sourceRetrieved || '',
                      })}><SaveIcon fontSize="small" /></IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => remove(v.id)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {draft && (
        <Box sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 2 }}>
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField size="small" label="Version" required sx={{ flex: 1 }}
                placeholder="2402, 8.10, 2023 R2"
                value={draft.version}
                onChange={(e) => setDraft({ ...draft, version: e.target.value })} />
              <TextField size="small" label="Released" type="date" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }} value={draft.releaseDate}
                onChange={(e) => setDraft({ ...draft, releaseDate: e.target.value })} />
              <TextField size="small" label="End of life" type="date" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }} value={draft.eolDate}
                onChange={(e) => setDraft({ ...draft, eolDate: e.target.value })} />
              <TextField size="small" label="End of support" type="date" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }} value={draft.eosDate}
                onChange={(e) => setDraft({ ...draft, eosDate: e.target.value })} />
            </Stack>
            <TextField size="small" label="Notes" fullWidth multiline minRows={2}
              value={draft.notes}
              onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
            {/* Asked for beside the dates, not on another screen. These dates
                get quoted to clients and put into migration plans. */}
            <Alert severity="info">
              A support date is a vendor fact. Record where it came from, so the next person
              can check it rather than trust it.
            </Alert>
            <Stack direction="row" spacing={2}>
              <TextField size="small" label="Source URL" sx={{ flex: 2 }}
                value={draft.sourceUrl}
                onChange={(e) => setDraft({ ...draft, sourceUrl: e.target.value })} />
              <TextField size="small" label="Retrieved" type="date" sx={{ flex: 1 }}
                InputLabelProps={{ shrink: true }} value={draft.sourceRetrieved}
                onChange={(e) => setDraft({ ...draft, sourceRetrieved: e.target.value })} />
            </Stack>
            <TextField size="small" label="Verbatim quote from the vendor page" fullWidth
              multiline minRows={2} value={draft.sourceQuote}
              onChange={(e) => setDraft({ ...draft, sourceQuote: e.target.value })} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button size="small" onClick={() => setDraft(null)} disabled={busy}>Cancel</Button>
              <Button size="small" variant="contained" onClick={save}
                disabled={busy || !draft.version.trim()}>
                {busy ? 'Saving…' : 'Save version'}
              </Button>
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              Saving the catalogue entry at the bottom of this dialog also saves this version.
            </Typography>
            <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ display: 'none' }}>
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
}
